import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { handleFireBaseUpload } from "../Firebase/firestore";
import { slugify, generateSku, autoCanonicalUrl } from "../Funtions/seo";
import { homeOf, pathOfStyle } from "../Data/taxonomy";
import {
    UNIFORM_CATEGORY,
    DEFAULT_BULK_FROM,
    TEAM_FROM,
    UNIFORM_GROUPS,
    UNIFORM_GARMENTS,
    UNIFORM_SECTORS,
    LOGO_POSITIONS,
    LOGO_METHODS,
    NAME_COLORS,
    NAME_PLACES,
    SIZE_RUNS,
    SIZE_RUN_OPTIONS,
    SLEEVE_OPTIONS,
    sleeveOf,
    sizeRunOf,
    garmentByKey,
    positionByKey,
    uniformQuote,
    uniformLabel,
    pieceLabel,
    SET_GARMENT,
    logoSizeIn,
    kitOf,
} from "../Data/uniformCatalog";
import FabricLibraryPicker, { FabricSlot, fabricRefOf, fabricText, firstColorOf } from "./FabricLibraryPicker";
import "./uniform_upload.css";

// Upload / edit a uniform. Uniforms live in Presets like every product
// (productKind "uniform"), so the website's product page, search, sitemap,
// cart and orders all work for them; this form only asks what a uniform
// needs. Spec: docs/uniform-plan.md in the website repo.

const VIEW_PRODUCT_ADMIN_INDEX = 8;
const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : 0;
};
const blankRow = (position, piece = "") => ({
    position,
    piece,
    sizeIn: positionByKey(position)?.sizeIn || 3,
    embroidery: { on: false, perPiece: "", setup: "" },
    print: { on: false, perPiece: "", setup: "" },
    defaultMethod: "embroidery",
});
const rowFromDoc = (r) => ({
    position: r.position,
    piece: r.piece || "",
    sizeIn: String(logoSizeIn(r) || 3),
    embroidery: r.methods?.embroidery
        ? { on: true, perPiece: String(r.methods.embroidery.perPiece ?? ""), setup: String(r.methods.embroidery.setup ?? "") }
        : { on: false, perPiece: "", setup: "" },
    print: r.methods?.print
        ? { on: true, perPiece: String(r.methods.print.perPiece ?? ""), setup: String(r.methods.print.setup ?? "") }
        : { on: false, perPiece: "", setup: "" },
    defaultMethod: r.defaultMethod || "embroidery",
});
const rowToDoc = (r) => {
    const methods = {};
    LOGO_METHODS.forEach((m) => {
        if (r[m.key].on && num(r[m.key].perPiece) > 0) methods[m.key] = { perPiece: num(r[m.key].perPiece), setup: num(r[m.key].setup) };
    });
    const keys = Object.keys(methods);
    const out = { position: r.position, sizeIn: Number(r.sizeIn) || 0, methods, defaultMethod: methods[r.defaultMethod] ? r.defaultMethod : keys[0] || "" };
    if (r.piece) out.piece = r.piece;
    return out;
};

// A set (shirt + trouser, chef coat + pant + cap) lists its pieces; each
// piece has its own sizes and, optionally, its own price to sell it alone.
const blankPiece = (key) => {
    const g = garmentByKey(key);
    return { key, sizes: g ? SIZE_RUNS[g.sizeRun] : [], price1: "", price10: "", material: "", colorName: "", washcare: "", libraryFabric: null };
};

// The usual care lines for uniforms; anything else goes in "Other".
const WASH_CARE = [
    "Machine wash warm, iron medium",
    "Machine wash cold, gentle cycle",
    "Hand wash cold, do not bleach",
    "Dry clean only",
];

// Wash care as a dropdown; "Other" opens a box for a line of your own.
function WashCareField({ value, onChange }) {
    const preset = WASH_CARE.includes(value);
    const [other, setOther] = useState(!!value && !preset);
    const choice = preset ? value : other ? "__other" : "";
    return (
        <label>Wash care
            <select value={choice} onChange={(e) => {
                const v = e.target.value;
                setOther(v === "__other");
                onChange(v === "__other" ? (preset ? "" : value) : v);
            }}>
                <option value="">Choose wash care</option>
                {WASH_CARE.map((w) => <option key={w} value={w}>{w}</option>)}
                <option value="__other">Other (type it in)</option>
            </select>
            {choice === "__other" && (
                <input value={value} placeholder="e.g. Wash dark colours separately" onChange={(e) => onChange(e.target.value)} />
            )}
        </label>
    );
}

const EMPTY = {
    // "Does this uniform take a logo?" Off: sold plain, no logo on the site.
    logoEnabled: true,
    isSet: false,
    pieces: [],
    garment: "",
    sectors: [],
    name: "",
    des: "",
    longDescription: "",
    material: "",
    colorName: "",
    washcare: "",
    // The fabric picked from the fabric library, if any: { id, name, brand, src, material }.
    libraryFabric: null,
    mrp: "",
    price1: "",
    price10: "",
    bulkFrom: String(DEFAULT_BULK_FROM),
    sizes: [],
    customMeasure: true,
    names: { enabled: false, position: "right_chest", piece: "", perPiece: "", maxChars: "20", colors: NAME_COLORS.map((c) => c.key) },
    status: "draft",
    slug: "",
    metaTitle: "",
    metaDescription: "",
};

export default function UniformUpload() {
    const location = useLocation();
    const navigate = useNavigate();
    const editing = location.state && location.state.editPreset && location.state.editPreset.productKind === "uniform"
        ? location.state.editPreset
        : null;
    const baselineRef = useRef(editing ? { ...editing } : null);

    const [f, setF] = useState(EMPTY);
    const [rows, setRows] = useState([]);
    const [faqs, setFaqs] = useState([{ q: "", a: "" }]);
    const [photos, setPhotos] = useState([]); // [{ url?, file?, preview, alt }]
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState("");
    const fileRef = useRef(null);

    const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

    // Full / half / sleeveless: decides which sleeve measurements a customer
    // on custom measurements is asked for. Defaults from the garment.
    const sleeveChips = (item, garmentKey, onPick) => {
        const cur = sleeveOf(item, garmentKey);
        return (
            <div className="uu-chips" role="radiogroup" aria-label="Sleeves">
                {SLEEVE_OPTIONS.map((o) => (
                    <button type="button" key={o.key} role="radio" aria-checked={cur === o.key}
                        className={`uu-chip uu-chip--sm ${cur === o.key ? "is-on" : ""}`} onClick={() => onPick(o.key)}>{o.label}</button>
                ))}
            </div>
        );
    };

    // Fabric library picker: null, "single", or a piece's index in a set.
    const [fabricFor, setFabricFor] = useState(null);
    const closeFabricPicker = useCallback(() => setFabricFor(null), []);
    const pickLibraryFabric = (lib) => {
        const fill = { libraryFabric: fabricRefOf(lib), material: fabricText(lib) };
        const colour = firstColorOf(lib);
        setF((prev) => {
            if (fabricFor === "single") return { ...prev, ...fill, colorName: colour || prev.colorName };
            return {
                ...prev,
                pieces: prev.pieces.map((y, j) => (j === fabricFor ? { ...y, ...fill, colorName: colour || y.colorName } : y)),
            };
        });
        setFabricFor(null);
    };
    // Fabric and colour for one piece (or the single garment): a library
    // fabric brings both, so the boxes show only when none is picked.
    const fabricFields = (x, target, patch, colourHint) => {
        const lib = x.libraryFabric;
        const colours = lib ? String(lib.color || "").split(",").map((c) => c.trim()).filter(Boolean) : [];
        return (
            <>
                <div className="uu-libfab">
                    <FabricSlot
                        fabric={lib}
                        onOpen={() => setFabricFor(target)}
                        onClear={() => patch({ libraryFabric: null, material: "", colorName: "" })}
                    />
                    {!lib && <small className="uu-libfab__or">Not in the library? Type it in below.</small>}
                </div>
                {lib ? (
                    colours.length > 1 && (
                        <div className="uu-libfab__colours">
                            <span>Colour</span>
                            <div className="uu-chips" role="radiogroup" aria-label="Colour">
                                {colours.map((c) => (
                                    <button key={c} type="button" role="radio" aria-checked={x.colorName === c}
                                        className={`uu-chip ${x.colorName === c ? "is-on" : ""}`} onClick={() => patch({ colorName: c })}>{c}</button>
                                ))}
                            </div>
                        </div>
                    )
                ) : (
                    <>
                        <label>Fabric<input value={x.material} placeholder="65/35 poly-cotton twill, 210 GSM" onChange={(e) => patch({ material: e.target.value })} /></label>
                        <label>Colour<input value={x.colorName} placeholder={colourHint} onChange={(e) => patch({ colorName: e.target.value })} /></label>
                    </>
                )}
            </>
        );
    };

    // Load the product being edited.
    useEffect(() => {
        if (!editing) return;
        const p = editing;
        const savedPieces = Array.isArray(p.setPieces) ? p.setPieces : [];
        setF({
            ...EMPTY,
            isSet: savedPieces.length > 0,
            logoEnabled: p.logoEnabled !== false,
            pieces: savedPieces.map((x) => ({
                key: x.key,
                sizes: SIZE_RUNS[sizeRunOf(x, x.key)],
                price1: x.price1 ? String(x.price1) : "",
                price10: x.price10 ? String(x.price10) : "",
                material: x.material || "",
                colorName: x.colorName || "",
                washcare: x.washcare || "",
                libraryFabric: x.libraryFabric || null,
                sleeve: x.sleeve || "",
            })),
            garment: savedPieces.length ? "" : p.garment || "",
            sectors: Array.isArray(p.sectors) ? p.sectors : [],
            name: p.name || "",
            des: p.des || "",
            longDescription: p.longDescription || "",
            material: p.material || "",
            colorName: p.colorName || "",
            washcare: p.washcare || "",
            libraryFabric: p.libraryFabric || null,
            sleeve: p.sleeve || "",
            mrp: p.price && p.price1 && Number(p.price) > Number(p.price1) ? String(p.price) : "",
            price1: String(p.price1 ?? ""),
            price10: String(p.price10 ?? ""),
            bulkFrom: String(p.bulkFrom || DEFAULT_BULK_FROM),
            sizes: savedPieces.length ? [] : SIZE_RUNS[sizeRunOf(p, p.garment)],
            customMeasure: p.customMeasure !== false,
            names: {
                ...EMPTY.names,
                ...(p.staffNames || {}),
                perPiece: String(p.staffNames?.perPiece ?? ""),
                maxChars: String(p.staffNames?.maxChars ?? "20"),
            },
            status: p.status || "draft",
            slug: p.slug || "",
            metaTitle: p.metaTitle || "",
            metaDescription: p.metaDescription || "",
        });
        setRows((Array.isArray(p.logoPositions) ? p.logoPositions : []).map(rowFromDoc));
        setFaqs(Array.isArray(p.faqs) && p.faqs.length ? p.faqs : [{ q: "", a: "" }]);
        const imgs = Array.isArray(p.images) && p.images.length
            ? p.images.map((im) => ({ url: im.url, preview: im.url, alt: im.alt || "" }))
            : [p.src, ...(Array.isArray(p.single) ? p.single : [])].filter(Boolean).map((url) => ({ url, preview: url, alt: "" }));
        setPhotos(imgs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Picking a garment prefills sectors, logo positions and sizes (only
    // while they are still empty, so an edit never loses its own values).
    const chooseGarment = (key) => {
        const g = garmentByKey(key);
        // A two-piece garment (scrub suit, track suit…) is a set of its
        // pieces, each sized on its own.
        const kit = kitOf(key);
        if (kit) {
            setF((prev) => ({
                ...prev,
                isSet: true,
                garment: "",
                pieces: kit.map(blankPiece),
                sectors: prev.sectors.length ? prev.sectors : g.defaultSectors,
            }));
            setRows(kit.flatMap((pk) => (garmentByKey(pk)?.logoPositions || []).map((pos) => blankRow(pos, pk))));
            return;
        }
        if (f.isSet) {
            // Toggle the piece; a new piece brings its sizes, sectors and
            // logo positions.
            const on = f.pieces.some((x) => x.key === key);
            setF((prev) => ({
                ...prev,
                pieces: on ? prev.pieces.filter((x) => x.key !== key) : [...prev.pieces, blankPiece(key)].slice(0, 6),
                sectors: on || !g ? prev.sectors : [...new Set([...prev.sectors, ...g.defaultSectors])],
            }));
            if (on) setRows((prev) => prev.filter((r) => r.piece !== key));
            else if (g) setRows((prev) => [...prev, ...g.logoPositions.map((pos) => blankRow(pos, key))]);
            return;
        }
        setF((prev) => ({
            ...prev,
            garment: key,
            sectors: prev.sectors.length ? prev.sectors : g ? g.defaultSectors : [],
            sizes: g ? SIZE_RUNS[g.sizeRun] : prev.sizes,
        }));
        if (g && !rows.length) setRows(g.logoPositions.map(blankRow));
    };

    const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
    const garment = garmentByKey(f.garment);
    const setPieces = f.isSet
        ? f.pieces.map((x) => ({
            key: x.key,
            sizes: x.sizes,
            sizeRun: sizeRunOf(x, x.key),
            sleeve: sleeveOf(x, x.key),
            price1: num(x.price1),
            price10: num(x.price10),
            material: (x.material || "").trim(),
            colorName: (x.colorName || "").trim(),
            washcare: (x.washcare || "").trim(),
            libraryFabric: x.libraryFabric || null,
        }))
        : [];
    const productDoc = useMemo(() => ({
        productKind: "uniform",
        category: UNIFORM_CATEGORY,
        setPieces,
        sectors: f.sectors,
        price1: num(f.price1),
        price10: num(f.price10),
        bulkFrom: num(f.bulkFrom) || DEFAULT_BULK_FROM,
        logoEnabled: f.logoEnabled,
        logoPositions: f.logoEnabled ? rows.map(rowToDoc) : [],
        staffNames: { enabled: f.names.enabled, perPiece: num(f.names.perPiece), piece: f.names.piece },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [f, rows]);
    const joinPieces = (field) => setPieces
        .filter((x) => x[field])
        .map((x) => `${pieceLabel(x.key)}: ${x[field]}`)
        .join(" · ");
    const kindLabel = f.isSet ? uniformLabel({ setPieces }) : garment ? garment.label : "uniform";
    const placement = homeOf(productDoc);

    // Example price the customer would see, for a quick sense check.
    const example = useMemo(() => {
        const first = productDoc.logoPositions.find((r) => r.defaultMethod);
        const logos = first ? [{ key: first.piece ? `${first.piece}:${first.position}` : first.position, position: first.position, on: true, method: first.defaultMethod, logoId: "x" }] : [];
        const cfg = (n) => (f.isSet
            ? { mode: "set", rows: [{ sizes: Object.fromEntries(setPieces.map((x) => [x.key, x.sizes[0]])), qty: n, names: [] }], logos }
            : { sizes: [{ size: "M", qty: n, names: [] }], logos });
        return [1, TEAM_FROM].map((n) => ({ n, q: uniformQuote(productDoc, cfg(n)) }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productDoc]);

    const altsOk = photos.length > 0 && photos.every((p) => p.alt.trim());
    const blockers = [
        !f.isSet && !f.garment && "Garment",
        f.isSet && f.pieces.length < 2 && "At least two pieces in the set",
        f.isSet && f.pieces.some((x) => !x.sizes.length) && "Sizes for every piece of the set",
        f.isSet && f.pieces.some((x) => num(x.price10) > num(x.price1) && num(x.price1) > 0) && "A piece's 10+ price should not be higher than its single price",
        !f.sectors.length && "At least one sector",
        !f.name.trim() && "Name",
        !(num(f.price1) > 0) && "Price for 1–9 pieces",
        !(num(f.price10) > 0) && "Price for 10+ pieces",
        num(f.price10) > num(f.price1) && "10+ price should not be higher than the single-piece price",
        photos.length < 1 && "At least one photo of the uniform",
        photos.length > 0 && !altsOk && "Alt text for every photo",
        !f.isSet && !f.sizes.length && "At least one size",
        f.logoEnabled && !rows.length && "At least one logo position (or switch the logo off in section 4)",
        f.logoEnabled && rows.some((r) => !LOGO_METHODS.some((m) => r[m.key].on && num(r[m.key].perPiece) > 0)) && "Every logo position needs a method with a per-piece price",
        f.names.enabled && !(num(f.names.perPiece) > 0) && "Staff name price",
    ].filter(Boolean);
    const canPublish = blockers.length === 0;

    const addFiles = (files) => {
        const next = [...files].map((file) => ({ file, preview: URL.createObjectURL(file), alt: "" }));
        setPhotos((prev) => [...prev, ...next]);
    };
    // Drag a photo onto another to move it there; the first one is the main photo.
    const [dragFrom, setDragFrom] = useState(null);
    const [dragOver, setDragOver] = useState(null);
    const dropPhoto = (to) => {
        if (dragFrom !== null && dragFrom !== to) {
            setPhotos((prev) => {
                const a = prev.slice();
                const [moved] = a.splice(dragFrom, 1);
                a.splice(to, 0, moved);
                return a;
            });
        }
        setDragFrom(null);
        setDragOver(null);
    };
    const movePhoto = (i, d) => setPhotos((prev) => {
        const a = prev.slice();
        const j = i + d;
        if (j < 0 || j >= a.length) return prev;
        [a[i], a[j]] = [a[j], a[i]];
        return a;
    });

    // The button says what happens: publish puts it on the website, draft
    // keeps it in the panel only.
    const save = async (status = f.status) => {
        if (status === "published" && !canPublish) {
            setNote("Fix the items in the checklist, or save as a draft.");
            return;
        }
        setBusy(true);
        setNote("Uploading photos…");
        try {
            const col = db.collection("Presets");
            const docId = editing ? editing.doc : col.doc().id;
            const urls = [];
            for (const p of photos) {
                if (p.url) urls.push(p.url);
                else urls.push(await handleFireBaseUpload(`${p.file.name}-${Date.now()}`, p.file, "presets/uniforms"));
            }
            setNote("Saving…");
            const name = f.name.trim();
            const slug = f.slug.trim() || slugify(name);
            const price1 = num(f.price1);
            const mrp = num(f.mrp) > price1 ? num(f.mrp) : price1;
            const sectorLabels = f.sectors.map((s) => UNIFORM_SECTORS.find((x) => x.key === s)?.label).filter(Boolean);
            const baseline = { ...(baselineRef.current || {}) };
            delete baseline.doc;
            const now = new Date().toISOString();
            const Data = {
                ...baseline,
                name,
                des: f.des.trim(),
                longDescription: f.longDescription.trim(),
                faqs: faqs.filter((x) => x.q.trim() && x.a.trim()).map((x) => ({ q: x.q.trim(), a: x.a.trim() })),
                category: UNIFORM_CATEGORY,
                productKind: "uniform",
                garment: f.isSet ? SET_GARMENT : f.garment,
                setPieces,
                sectors: f.sectors,
                department: placement ? placement.department.slug : "uniform",
                style: placement ? placement.style.slug : "",
                sizing: "body",
                // price/priceFinal keep cards, search and sitemaps working
                price: mrp,
                discount: mrp > price1 ? Math.round(((mrp - price1) / mrp) * 100) : 0,
                priceFinal: price1,
                price1,
                price10: num(f.price10),
                bulkFrom: num(f.bulkFrom) || DEFAULT_BULK_FROM,
                sizes: f.isSet ? [] : f.sizes,
                sizeRun: f.isSet ? "" : sizeRunOf(f, f.garment),
                sleeve: f.isSet ? "" : sleeveOf(f, f.garment),
                customMeasure: !!f.customMeasure,
                logoEnabled: !!f.logoEnabled,
                logoPositions: f.logoEnabled ? rows.map(rowToDoc).filter((r) => Object.keys(r.methods).length) : [],
                staffNames: {
                    enabled: !!f.names.enabled,
                    position: f.names.position,
                    piece: f.isSet ? f.names.piece || (f.pieces[0] && f.pieces[0].key) || "" : "",
                    perPiece: num(f.names.perPiece),
                    maxChars: num(f.names.maxChars) || 20,
                    colors: f.names.colors,
                },
                // A set keeps fabric, colour and care per piece; the joined
                // lines keep search, cards and Google working as before.
                material: f.isSet ? joinPieces("material") : f.material.trim(),
                colorName: f.isSet ? joinPieces("colorName") : f.colorName.trim(),
                washcare: f.isSet ? joinPieces("washcare") : f.washcare.trim(),
                libraryFabric: f.isSet ? null : f.libraryFabric || null,
                washcarePieces: f.isSet
                    ? Object.fromEntries(setPieces.filter((x) => x.washcare).map((x) => [pieceLabel(x.key), x.washcare]))
                    : {},
                measurementProfile: "uniform",
                deliveryDays: 10,
                availability: "MadeToOrder",
                currency: "INR",
                gender: "unisex",
                src: urls[0] || "",
                single: urls.slice(1),
                images: urls.map((url, i) => ({ url, alt: photos[i].alt.trim() || `${name} – view ${i + 1}`, isPrimary: i === 0 || undefined })),
                ogImage: urls[0] || "",
                slug,
                sku: baseline.sku || generateSku("uniform"),
                metaTitle: f.metaTitle.trim() || `${name} with Your Logo | Maviinci Uniforms`.slice(0, 70),
                metaDescription: f.metaDescription.trim()
                    || `${name}${sectorLabels.length ? ` for ${sectorLabels.slice(0, 3).join(", ")}` : ""}. From 1 piece, lower rates from ${TEAM_FROM}, with your logo embroidered or printed.`.slice(0, 158),
                keywords: [name.toLowerCase(), `${kindLabel.toLowerCase()} with logo`, ...sectorLabels.map((s) => `${s.toLowerCase()} uniform`)].slice(0, 10),
                canonicalUrl: autoCanonicalUrl({ category: UNIFORM_CATEGORY, slug, docId }),
                status,
                noindex: false,
                updatedAt: now,
            };
            if (status === "published") Data.publishedAt = baseline.publishedAt || now;
            if (!editing) Data.created = new Date().toString();
            Object.keys(Data).forEach((k) => Data[k] === undefined && delete Data[k]);
            Data.images = Data.images.map((im) => (im.isPrimary ? im : { url: im.url, alt: im.alt }));
            await col.doc(docId).set(Data);
            set({ status });
            setNote(status === "published" ? "Published. It is live on the website." : status === "archived" ? "Archived. Hidden from the website." : "Saved as a draft. Not on the website yet.");
            setBusy(false);
            if (!editing) {
                setF(EMPTY);
                setRows([]);
                setFaqs([{ q: "", a: "" }]);
                setPhotos([]);
            }
        } catch (e) {
            setNote(`Not saved: ${e.message}`);
            setBusy(false);
        }
    };

    const methodRow = (r, i, m) => (
        <div className={`uu-method ${r[m.key].on ? "is-on" : ""}`} key={m.key}>
            <label className="uu-check">
                <input
                    type="checkbox"
                    checked={r[m.key].on}
                    onChange={(e) => setRows((prev) => prev.map((x, j) => (j === i ? { ...x, [m.key]: { ...x[m.key], on: e.target.checked } } : x)))}
                />
                {m.label}
            </label>
            {r[m.key].on && (
                <>
                    <label>Per piece ₹
                        <input inputMode="numeric" value={r[m.key].perPiece} placeholder="20"
                            onChange={(e) => setRows((prev) => prev.map((x, j) => (j === i ? { ...x, [m.key]: { ...x[m.key], perPiece: e.target.value } } : x)))} />
                    </label>
                    <label>One-time setup ₹
                        <input inputMode="numeric" value={r[m.key].setup} placeholder="100"
                            onChange={(e) => setRows((prev) => prev.map((x, j) => (j === i ? { ...x, [m.key]: { ...x[m.key], setup: e.target.value } } : x)))} />
                    </label>
                    <label className="uu-radio">
                        <input type="radio" name={`def-${i}`} checked={r.defaultMethod === m.key}
                            onChange={() => setRows((prev) => prev.map((x, j) => (j === i ? { ...x, defaultMethod: m.key } : x)))} />
                        On the photo
                    </label>
                </>
            )}
        </div>
    );

    return (
        <div className="uu">
            <p className="uu-lead">
                One uniform, uploaded once, shows on every sector page you tick. Customers buy 1 piece, 10+ at your team
                rate, or ask for a quote from the bulk quantity. Logo and staff-name prices are added on top, exactly as
                you set them here.
            </p>
            {editing && <p className="uu-note">Editing <b>{editing.name}</b>.</p>}

            {/* 1. Garment */}
            <section className="uu-sec">
                <h3>1. What is it?</h3>
                <div className="uu-chips">
                    <button type="button" className={`uu-chip ${!f.isSet ? "is-on" : ""}`} onClick={() => set({ isSet: false })}>One garment</button>
                    <button type="button" className={`uu-chip ${f.isSet ? "is-on" : ""}`} onClick={() => set({ isSet: true, pieces: f.pieces.length ? f.pieces : f.garment ? [blankPiece(f.garment)] : [] })}>
                        A set (shirt + trouser, chef coat + pant…)
                    </button>
                </div>
                {f.isSet && (
                    <p className="uu-hint">
                        Tap every piece in the set. {f.pieces.length ? <b>{uniformLabel({ setPieces })}</b> : "Nothing picked yet."}
                    </p>
                )}
                {UNIFORM_GROUPS.map((g) => (
                    <div key={g.key} className="uu-group">
                        <span className="uu-group__label">{g.label}</span>
                        <div className="uu-chips">
                            {UNIFORM_GARMENTS.filter((x) => x.group === g.key).map((x) => (
                                <button type="button" key={x.key} className={`uu-chip ${(x.kit ? f.isSet && f.pieces.length === x.kit.length && x.kit.every((k) => f.pieces.some((y) => y.key === k)) : f.isSet ? f.pieces.some((y) => y.key === x.key) : f.garment === x.key) ? "is-on" : ""}`}
                                    title={x.kit ? `A set: ${x.kit.map((k) => garmentByKey(k)?.label).join(" + ")}` : undefined}
                                    onClick={() => chooseGarment(x.key)}>
                                    {x.label}{x.kit ? <small> (set)</small> : null}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </section>

            {/* 2. Sectors */}
            <section className="uu-sec">
                <h3>2. Show on these pages</h3>
                <div className="uu-chips">
                    {UNIFORM_SECTORS.map((s) => (
                        <button type="button" key={s.key} className={`uu-chip ${f.sectors.includes(s.key) ? "is-on" : ""}`}
                            onClick={() => set({ sectors: toggle(f.sectors, s.key) })}>
                            {s.label}{s.parent ? <small> (in {UNIFORM_SECTORS.find((x) => x.key === s.parent).label})</small> : null}
                        </button>
                    ))}
                </div>
                {placement && (
                    <p className="uu-hint">Main page: <code>maviinci.in{pathOfStyle("uniform", placement.style.slug)}</code>, and every other ticked sector.</p>
                )}
            </section>

            {/* 3. Details */}
            <section className="uu-sec uu-grid">
                <h3>3. Name and details</h3>
                <label>Name<input value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="White Chef Coat with Black Piping" /></label>
                {f.isSet ? (
                    <div className="uu-wide uu-pieces">
                        <span className="uu-group__label">Fabric, colour and care for each piece</span>
                        {f.pieces.map((x, i) => (
                            <div key={x.key} className="uu-piece-row uu-piece-row--wide">
                                <b>{pieceLabel(x.key)}</b>
                                {/(trouser|pant|skirt|salwar|apron|tie|cap|scarf)/.test(x.key) ? null
                                    : sleeveChips(x, x.key, (v) => setF((prev) => ({ ...prev, pieces: prev.pieces.map((y, j) => (j === i ? { ...y, sleeve: v } : y)) })))}
                                {fabricFields(x, i, (patch) => setF((prev) => ({ ...prev, pieces: prev.pieces.map((y, j) => (j === i ? { ...y, ...patch } : y)) })), "Sky blue")}
                                <WashCareField value={x.washcare}
                                    onChange={(v) => setF((prev) => ({ ...prev, pieces: prev.pieces.map((y, j) => (j === i ? { ...y, washcare: v } : y)) }))} />
                            </div>
                        ))}
                        {!f.pieces.length && <p className="uu-hint">Pick the pieces of the set in section 1 first.</p>}
                    </div>
                ) : (
                    <>
                        {f.garment && !/(trouser|pant|skirt|salwar|apron|tie|cap|scarf)/.test(f.garment) && (
                            <div className="uu-wide">{sleeveChips(f, f.garment, (v) => set({ sleeve: v }))}</div>
                        )}
                        <div className="uu-wide uu-piece-row">{fabricFields(f, "single", set, "White")}</div>
                        <WashCareField value={f.washcare} onChange={(v) => set({ washcare: v })} />
                    </>
                )}
                <label className="uu-wide">Short description<textarea rows={2} value={f.des} onChange={(e) => set({ des: e.target.value })} /></label>
                <label className="uu-wide">Long description (fit, fabric, finishing)<textarea rows={5} value={f.longDescription} onChange={(e) => set({ longDescription: e.target.value })} /></label>
            </section>
            {fabricFor !== null && (
                <FabricLibraryPicker
                    item={fabricFor === "single" ? (f.name || "this uniform") : pieceLabel((f.pieces[fabricFor] || {}).key)}
                    selectedId={((fabricFor === "single" ? f.libraryFabric : (f.pieces[fabricFor] || {}).libraryFabric) || {}).id}
                    onPick={pickLibraryFabric}
                    onClose={closeFabricPicker}
                />
            )}

            {/* 4. Photos */}
            <section className="uu-sec">
                <h3>4. Photos <small>{f.logoEnabled ? 'your "YOUR LOGO" photo first; drag photos to change the order' : "add one or more photos; drag to change the order, the first is the main photo"}</small></h3>
                <div className="uu-logo-switch">
                    <span>Does this uniform take a logo?</span>
                    <div className="uu-chips" role="radiogroup" aria-label="Takes a logo">
                        <button type="button" role="radio" aria-checked={f.logoEnabled} className={`uu-chip ${f.logoEnabled ? "is-on" : ""}`} onClick={() => set({ logoEnabled: true })}>Yes, with a logo</button>
                        <button type="button" role="radio" aria-checked={!f.logoEnabled} className={`uu-chip ${!f.logoEnabled ? "is-on" : ""}`} onClick={() => set({ logoEnabled: false })}>No, sold plain</button>
                    </div>
                    <small>
                        {f.logoEnabled
                            ? "Customers upload their logo. Set the places and prices in section 7."
                            : "No logo option on the website for this uniform."}
                    </small>
                </div>
                <div className="uu-photos">
                    {photos.map((p, i) => (
                        <figure key={p.preview || p.url || i}
                            className={`uu-photo ${dragFrom === i ? "is-dragging" : ""} ${dragOver === i && dragFrom !== null && dragFrom !== i ? "is-drop" : ""}`}
                            draggable={photos.length > 1}
                            onDragStart={(e) => { setDragFrom(i); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", String(i)); } catch (_) {} }}
                            onDragOver={(e) => { e.preventDefault(); if (dragOver !== i) setDragOver(i); }}
                            onDrop={(e) => { e.preventDefault(); dropPhoto(i); }}
                            onDragEnd={() => { setDragFrom(null); setDragOver(null); }}>
                            <div className="uu-photo__frame">
                                <img src={p.preview} alt="" draggable={false} />
                                <span className="uu-photo__num">{i + 1}</span>
                                {i === 0 && <span className="uu-photo__main">Main</span>}
                            </div>
                            <input placeholder="Alt text (what the photo shows)" value={p.alt} draggable={false}
                                onDragStart={(e) => e.preventDefault()}
                                onChange={(e) => setPhotos((prev) => prev.map((x, j) => (j === i ? { ...x, alt: e.target.value } : x)))} />
                            <div className="uu-photo__btns">
                                <button type="button" onClick={() => movePhoto(i, -1)} disabled={i === 0}>←</button>
                                <button type="button" onClick={() => movePhoto(i, 1)} disabled={i === photos.length - 1}>→</button>
                                <button type="button" onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}>Remove</button>
                            </div>
                        </figure>
                    ))}
                    <button type="button" className="uu-add" onClick={() => fileRef.current && fileRef.current.click()}>+ Add photos</button>
                    <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                </div>
            </section>

            {/* 5. Price */}
            <section className="uu-sec uu-grid">
                <h3>5. Price {f.isSet ? "per set" : "per piece"}</h3>
                <label>{f.isSet ? "Full set, 1–9" : "1–9 pieces"} ₹<input inputMode="numeric" value={f.price1} onChange={(e) => set({ price1: e.target.value })} placeholder="899" /></label>
                <label>{f.isSet ? `Full set, ${TEAM_FROM}+` : `${TEAM_FROM}+ pieces`} ₹<input inputMode="numeric" value={f.price10} onChange={(e) => set({ price10: e.target.value })} placeholder="749" /></label>
                <label>Quote from (pieces)<input inputMode="numeric" value={f.bulkFrom} onChange={(e) => set({ bulkFrom: e.target.value })} /></label>
                <label>MRP ₹ <small>(optional, shown struck through)</small><input inputMode="numeric" value={f.mrp} onChange={(e) => set({ mrp: e.target.value })} /></label>
                {f.isSet && f.pieces.length > 0 && (
                    <div className="uu-wide uu-pieces">
                        <span className="uu-group__label">Sell a piece on its own? Leave empty to sell it only in the set</span>
                        {f.pieces.map((x, i) => (
                            <div key={x.key} className="uu-piece-row">
                                <b>{pieceLabel(x.key)}</b>
                                <label>1–9 ₹<input inputMode="numeric" value={x.price1}
                                    onChange={(e) => set({ pieces: f.pieces.map((y, j) => (j === i ? { ...y, price1: e.target.value } : y)) })} /></label>
                                <label>{TEAM_FROM}+ ₹<input inputMode="numeric" value={x.price10}
                                    onChange={(e) => set({ pieces: f.pieces.map((y, j) => (j === i ? { ...y, price10: e.target.value } : y)) })} /></label>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* 6. Sizes: every size is stitched to order, so only the run is chosen */}
            <section className="uu-sec">
                <h3>6. Sizes <small>every size is made to order, so customers get the whole range</small></h3>
                {(f.isSet ? f.pieces.map((x, i) => ({ x, i, label: pieceLabel(x.key) })) : [{ x: f, i: -1, label: "" }]).map(({ x, i, label }) => {
                    const run = sizeRunOf(x, i < 0 ? f.garment : x.key);
                    const pick = (k) => (i < 0
                        ? set({ sizes: SIZE_RUNS[k] })
                        : set({ pieces: f.pieces.map((y, j) => (j === i ? { ...y, sizes: SIZE_RUNS[k] } : y)) }));
                    return (
                        <div key={label || "one"} className="uu-group">
                            {label && <span className="uu-group__label">{label}</span>}
                            <div className="uu-chips" role="radiogroup" aria-label={`Size range${label ? ` for ${label}` : ""}`}>
                                {SIZE_RUN_OPTIONS.map((o) => (
                                    <button type="button" key={o.key} role="radio" aria-checked={run === o.key}
                                        className={`uu-chip ${run === o.key ? "is-on" : ""}`} onClick={() => pick(o.key)}>
                                        {o.label} <small>{o.range}</small>
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })}
                <p className="uu-hint">The size chart and AI Find my size on the website use this range. Customers who fall outside it can give their own measurements.</p>
                <label className="uu-check">
                    <input type="checkbox" checked={f.customMeasure} onChange={(e) => set({ customMeasure: e.target.checked })} />
                    Customers may give their own measurements instead of a size
                </label>
            </section>

            {/* 7. Logo positions (only when the uniform takes a logo) */}
            {f.logoEnabled && (
            <section className="uu-sec">
                <h3>7. Logo positions <small>per-piece rate + one-time setup (die / digitizing), as it costs you</small></h3>
                {rows.map((r, i) => (
                    <div key={`${r.position}-${i}`} className="uu-logo">
                        <div className="uu-logo__head">
                            {f.isSet && (
                                <select value={r.piece} aria-label="Piece" onChange={(e) => setRows((prev) => prev.map((x, j) => (j === i ? { ...x, piece: e.target.value } : x)))}>
                                    {f.pieces.map((x) => <option key={x.key} value={x.key}>{pieceLabel(x.key)}</option>)}
                                </select>
                            )}
                            <select value={r.position} onChange={(e) => setRows((prev) => prev.map((x, j) => (j === i ? { ...x, position: e.target.value } : x)))}>
                                {LOGO_POSITIONS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                            </select>
                            <label>Size (inches)<input inputMode="decimal" value={r.sizeIn}
                                onChange={(e) => setRows((prev) => prev.map((x, j) => (j === i ? { ...x, sizeIn: e.target.value } : x)))} /></label>
                            <button type="button" className="uu-link" onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}>Remove</button>
                        </div>
                        {LOGO_METHODS.map((m) => methodRow(r, i, m))}
                    </div>
                ))}
                <button type="button" className="uu-link" onClick={() => setRows((prev) => [...prev, blankRow("left_chest", f.isSet && f.pieces[0] ? f.pieces[0].key : "")])}>+ Add a logo position</button>
                <p className="uu-hint">The same logo, method and size at two positions shares one setup charge.</p>
            </section>
            )}

            {/* 8. Staff names */}
            <section className="uu-sec uu-grid">
                <h3>8. Staff names</h3>
                <label className="uu-check uu-wide">
                    <input type="checkbox" checked={f.names.enabled} onChange={(e) => set({ names: { ...f.names, enabled: e.target.checked } })} />
                    Customers can add a name on each piece
                </label>
                {f.names.enabled && (
                    <>
                        {f.isSet && (
                            <label>On which piece
                                <select value={f.names.piece || (f.pieces[0] && f.pieces[0].key) || ""} onChange={(e) => set({ names: { ...f.names, piece: e.target.value } })}>
                                    {f.pieces.map((x) => <option key={x.key} value={x.key}>{pieceLabel(x.key)}</option>)}
                                </select>
                            </label>
                        )}
                        <label>Where the name goes
                            <select value={f.names.position} onChange={(e) => set({ names: { ...f.names, position: e.target.value } })}>
                                {NAME_PLACES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                            </select>
                        </label>
                        <label>Per piece ₹<input inputMode="numeric" value={f.names.perPiece} onChange={(e) => set({ names: { ...f.names, perPiece: e.target.value } })} placeholder="49" /></label>
                        <label>Max letters<input inputMode="numeric" value={f.names.maxChars} onChange={(e) => set({ names: { ...f.names, maxChars: e.target.value } })} /></label>
                        <div className="uu-wide">
                            <span className="uu-group__label">Thread colours offered</span>
                            <div className="uu-chips">
                                {NAME_COLORS.map((c) => (
                                    <button type="button" key={c.key} className={`uu-chip uu-chip--sm ${f.names.colors.includes(c.key) ? "is-on" : ""}`}
                                        onClick={() => set({ names: { ...f.names, colors: toggle(f.names.colors, c.key) } })}>
                                        <span className="uu-swatch" style={{ background: c.hex }} />{c.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </section>

            {/* 9. FAQs + SEO */}
            <section className="uu-sec uu-grid">
                <h3>9. FAQs and Google <small>empty fields are written for you</small></h3>
                <label>URL slug<input value={f.slug} placeholder={slugify(f.name) || "auto"} onChange={(e) => set({ slug: slugify(e.target.value) })} /></label>
                <label>Page title<input value={f.metaTitle} placeholder={f.name ? `${f.name} with Your Logo | Maviinci Uniforms` : "auto"} onChange={(e) => set({ metaTitle: e.target.value })} /></label>
                <label className="uu-wide">Meta description<textarea rows={2} value={f.metaDescription} placeholder="auto" onChange={(e) => set({ metaDescription: e.target.value })} /></label>
                <div className="uu-wide uu-faqs">
                    {faqs.map((x, i) => (
                        <div key={i} className="uu-faq">
                            <input placeholder="Question" value={x.q} onChange={(e) => setFaqs((prev) => prev.map((y, j) => (j === i ? { ...y, q: e.target.value } : y)))} />
                            <textarea rows={2} placeholder="Answer" value={x.a} onChange={(e) => setFaqs((prev) => prev.map((y, j) => (j === i ? { ...y, a: e.target.value } : y)))} />
                        </div>
                    ))}
                    <button type="button" className="uu-link" onClick={() => setFaqs((prev) => [...prev, { q: "", a: "" }])}>+ Add a question</button>
                </div>
            </section>

            {/* Summary + save */}
            <section className="uu-sec uu-summary">
                <h3>Check and save</h3>
                <div className="uu-examples">
                    {example.map(({ n, q }) => (
                        <div key={n} className="uu-example">
                            <strong>{n} piece{n > 1 ? "s" : ""}</strong>
                            <span>₹{q.total.toLocaleString("en-IN")} total · ₹{q.perPiece.toLocaleString("en-IN")} per piece</span>
                            <small>garment ₹{q.garment} · logo ₹{q.logo} · setup ₹{q.setup}</small>
                        </div>
                    ))}
                </div>
                {blockers.length > 0 ? (
                    <ul className="uu-blockers">{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
                ) : (
                    <p className="uu-ok">Ready to publish.</p>
                )}
                <div className="uu-actions">
                    <button type="button" className="uu-save" disabled={busy || !canPublish} onClick={() => save("published")}
                        title={canPublish ? "" : "Finish the checklist above to publish"}>
                        {busy ? "Saving…" : editing && f.status === "published" ? "Save changes (live)" : "Publish on website"}
                    </button>
                    <button type="button" className="uu-save uu-save--ghost" disabled={busy} onClick={() => save("draft")}>
                        {editing && f.status === "published" ? "Unpublish (move to draft)" : "Save as draft"}
                    </button>
                    {editing && f.status !== "archived" && (
                        <button type="button" className="uu-link" disabled={busy} onClick={() => save("archived")}>Archive</button>
                    )}
                    {editing && <span className={`uu-status is-${f.status}`}>Now: {f.status === "published" ? "live on the website" : f.status}</span>}
                    {editing && <button type="button" className="uu-link" onClick={() => navigate(`/admin/${VIEW_PRODUCT_ADMIN_INDEX}`)}>Back to products</button>}
                    {note && <span className="uu-note-inline">{note}</span>}
                </div>
            </section>
        </div>
    );
}
